import { Segment, Selection } from "core/models";
import { getFileURL } from "core/models/Segment";

/**
 * Creates a HLS manifest in a string
 *
 * @export
 * @param {Segment[]} segments
 * @returns {string}
 */
function createHLSManifest(
    channelID: number,
    segments: Segment[],
): string | null {
    let targetDuration = 0;
    let extInfContents = "";

    // Target duration should be the biggest duration of 1 chunk.
    segments.forEach((segment) => {
        if (segment.tsLength > targetDuration) {
            targetDuration = Math.ceil(segment.tsLength);
        }

        const tsURL = new URL(
            getFileURL(channelID, segment.tsPath, segment.token),
        );
        extInfContents += `#EXTINF:${segment.tsLength}\n`;
        extInfContents += `${tsURL}\n`;
    });

    if (!extInfContents) {
        return null;
    }

    let manifest = "#EXTM3U\n";
    manifest += "#EXT-X-VERSION:4\n";
    manifest += "#EXT-X-PLAYLIST-TYPE:VOD\n";
    manifest += "#EXT-X-MEDIA-SEQUENCE:0\n";
    manifest += "#EXT-X-ALLOW-CACHE:YES\n";
    manifest += `#EXT-X-TARGETDURATION:${targetDuration}\n`;
    manifest += extInfContents;
    manifest += "#EXT-X-ENDLIST";

    return manifest;
}
export function createHLSManifestFromSelections(
    channelID: number,
    selections: Selection[],
): string | null {
    let targetDuration = 0;
    let extInfContents = "";

    // Target duration should be the biggest duration of 1 chunk.
    selections.forEach((selection) => {
        const segmentsReversed = [...selection.segments].reverse();
        segmentsReversed.forEach((segment) => {
            if (segment.tsLength > targetDuration) {
                targetDuration += Math.ceil(segment.tsLength);
            }
            const tsURL = new URL(
                getFileURL(channelID, segment.tsPath, segment.token),
            );
            extInfContents += `#EXTINF:${segment.tsLength}\n`;
            extInfContents += `${tsURL}\n`;
        });
        extInfContents += `#EXT-X-DISCONTINUITY\n`;
    });

    if (!extInfContents) {
        return null;
    }

    let manifest = "#EXTM3U\n";
    manifest += "#EXT-X-VERSION:4\n";
    manifest += "#EXT-X-PLAYLIST-TYPE:VOD\n";
    manifest += "#EXT-X-MEDIA-SEQUENCE:0\n";
    manifest += "#EXT-X-ALLOW-CACHE:YES\n";
    manifest += `#EXT-X-TARGETDURATION:${targetDuration}\n`;
    manifest += extInfContents;
    manifest += "#EXT-X-ENDLIST";

    return manifest;
}

/**
 * Creates a manifest URL that is playable.
 *
 * @export
 * @param {Segment[]} segments
 * @returns {string}
 */
function createHLSManifestObject(
    channelID: number,
    segments: Segment[],
): Blob | null {
    const manifestText = createHLSManifest(channelID, segments);
    if (manifestText === null) {
        return null;
    }
    return new Blob([manifestText], {
        type: "text/plain",
    });
}
export function createHLSManifestObjectFromSelections(
    channelID: number,
    selections: Selection[],
): Blob | null {
    const manifestText = createHLSManifestFromSelections(channelID, selections);
    if (manifestText === null) {
        return null;
    }
    return new Blob([manifestText], {
        type: "text/plain",
    });
}
export default createHLSManifestObject;
